
import { AlertCircle, Info, Megaphone, X } from 'lucide-react';
import { useState, useEffect } from 'react';

export type BannerType = 'info' | 'warning' | 'announcement';

export interface BannerSettings {
    active: boolean;
    type: BannerType;
    message: string;
}

interface AnnouncementBannerProps {
    settings: BannerSettings | null;
    className?: string;
    loading?: boolean;
}

export default function AnnouncementBanner({ settings, className = '', loading = false }: AnnouncementBannerProps) {
    const [isVisible, setIsVisible] = useState(true);

    // Reset visibility if settings change (e.g. message update)
    useEffect(() => {
        if (settings?.active) {
            setIsVisible(true);
        }
    }, [settings?.active, settings?.message, settings?.type]);

    if (loading || !settings || !settings.active || !isVisible || !settings.message) {
        return null;
    }

    const styles = {
        info: {
            bg: 'bg-blue-100',
            text: 'text-blue-900',
            icon: Info
        },
        warning: {
            bg: 'bg-red-100',
            text: 'text-red-900',
            icon: AlertCircle
        },
        announcement: {
            bg: 'bg-green-100',
            text: 'text-green-900',
            icon: Megaphone
        }
    };

    const style = styles[settings.type] || styles.info;
    const Icon = style.icon;

    return (
        <div className={`w-full ${style.bg} ${style.text} px-4 py-4 shadow-sm relative z-[60] ${className}`}>
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 justify-center">
                    <Icon className="w-6 h-6 flex-shrink-0" />
                    <p className="text-base font-bold leading-relaxed text-center">
                        {settings.message}
                    </p>
                </div>
                <button
                    onClick={() => setIsVisible(false)}
                    className="p-1 hover:bg-black/10 rounded-lg transition-colors flex-shrink-0"
                    aria-label="Close announcement"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
